import { MultiSelectOption } from '@/components/forms/multi-select/multi-select';
import { SelectOption } from '@/components/forms/select';
import { AddressModel, UtilityTypeIds, UtilityTypesOrderedList, AlertTypeIds, AlertTypes } from '@ncss/models/dist/v3';
import { computedAsync } from '@vueuse/core';
import {
  PropertyFilterGatewayStatusIds,
  PropertyFilterGatewayTypeIds,
  PropertyFilterInstallationStatusIds,
  PropertyListCountQuery,
} from 'common';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { PropertyService } from '@/http-services/property.service';
import { useUser } from './user.store';

export interface PropertyFilterForm {
  billingCompanyId?: string;
  address?: AddressModel;
  distanceFromCoords?: number;
  installationStatus?: PropertyFilterInstallationStatusIds;
  gatewayType?: PropertyFilterGatewayTypeIds;
  gatewayStatus?: PropertyFilterGatewayStatusIds;
  utilityTypes?: UtilityTypeIds[];
  alertTypes?: AlertTypeIds[];
}

export const InstallationStatusOptions: SelectOption<PropertyFilterInstallationStatusIds>[] = [
  { value: PropertyFilterInstallationStatusIds.COMPLETE, label: 'Complete' },
  { value: PropertyFilterInstallationStatusIds.ONGOING, label: 'Ongoing' },
];

export const GatewayStatusOptions: SelectOption<PropertyFilterGatewayStatusIds>[] = [
  { value: PropertyFilterGatewayStatusIds.ONLINE, label: 'Online' },
  { value: PropertyFilterGatewayStatusIds.OFFLINE, label: 'Offline' },
  { value: PropertyFilterGatewayStatusIds.CELLULAR_ENABLED, label: 'Cellular Enabled' },
];

export const GatewayTypeOptions: SelectOption<PropertyFilterGatewayTypeIds>[] = [
  { value: PropertyFilterGatewayTypeIds.NOT_PROGRAMMED, label: 'Not Programmed' },
  { value: PropertyFilterGatewayTypeIds.GW4, label: 'GW4' },
  { value: PropertyFilterGatewayTypeIds.GW4_LITE, label: 'GW4-Lite' },
  { value: PropertyFilterGatewayTypeIds.GW301, label: 'GW301' },
  { value: PropertyFilterGatewayTypeIds.GW301_LITE, label: 'GW301-Lite' },
  { value: PropertyFilterGatewayTypeIds.GW201, label: 'GW201' },
  { value: PropertyFilterGatewayTypeIds.MANUAL_READ, label: 'Manual Read' },
  { value: PropertyFilterGatewayTypeIds.THIRD_PARTY_CONNECTED, label: '3rd Party Connected' },
];

const MetricDistanceOptions: SelectOption<number>[] = [
  { value: 5, label: 'Within 5 km' },
  { value: 10, label: 'Within 10 km' },
  { value: 20, label: 'Within 20 km' },
  { value: 50, label: 'Within 50 km' },
  { value: 100, label: 'Within 100 km' },
];

const ImperialDistanceOptions: SelectOption<number>[] = [
  { value: 1, label: 'Within 1 mi' },
  { value: 5, label: 'Within 5 mi' },
  { value: 10, label: 'Within 10 mi' },
  { value: 20, label: 'Within 20 mi' },
  { value: 50, label: 'Within 50 mi' },
  { value: 100, label: 'Within 100 mi' },
];

export const UtilityTypeOptions: MultiSelectOption<UtilityTypeIds>[] = UtilityTypesOrderedList.map((ut) => {
  return {
    icon: ut.iconName,
    value: ut.id,
    activeColorHex: ut.colorHex,
  };
});

export const AlertTypeOptions: MultiSelectOption<AlertTypeIds>[] = [
  AlertTypes[AlertTypeIds.TAMPER],
  AlertTypes[AlertTypeIds.CONNECTION_ERROR],
  AlertTypes[AlertTypeIds.POWER_LOSS],
  AlertTypes[AlertTypeIds.BATTERY],
  AlertTypes[AlertTypeIds.TEMPERATURE],
  AlertTypes[AlertTypeIds.CONSUMPTION],
  AlertTypes[AlertTypeIds.LEAK],
].map((at) => {
  return {
    icon: at.iconName,
    value: at.id,
  };
});

function usePropertyFilterForm() {
  const user = useUser();
  const distanceUom = computed(() => (user?.useMetric ? 'km' : 'mi'));
  const DistanceOptions = computed(() => {
    if (user?.useMetric) {
      return MetricDistanceOptions;
    }
    return ImperialDistanceOptions;
  });
  const billingCompanyId = ref<string>();
  const installationStatus = ref<PropertyFilterInstallationStatusIds>();
  const gatewayStatus = ref<PropertyFilterGatewayStatusIds>();
  const gatewayType = ref<PropertyFilterGatewayTypeIds>();
  const address = ref<AddressModel>();
  const distanceFromCoords = ref<number>();
  const utilityTypes = ref<UtilityTypeIds[]>(UtilityTypesOrderedList.map((ut) => ut.id));
  const alertTypes = ref<AlertTypeIds[]>(AlertTypeOptions.map((at) => at.value));

  const hasFilterApplied = computed<boolean>(() => {
    return (
      !!billingCompanyId.value ||
      !!installationStatus.value ||
      !!gatewayStatus.value ||
      !!gatewayType.value ||
      !!address.value ||
      !!distanceFromCoords.value ||
      utilityTypes.value?.length !== UtilityTypeOptions.length ||
      alertTypes.value?.length !== AlertTypeOptions.length
    );
  });

  const filterForm = computed<PropertyFilterForm>(() => {
    return {
      billingCompanyId: billingCompanyId.value,
      installationStatus: installationStatus.value,
      gatewayStatus: gatewayStatus.value,
      gatewayType: gatewayType.value,
      address: address.value,
      distanceFromCoords: distanceFromCoords.value,
      utilityTypes: utilityTypes.value,
      alertTypes: alertTypes.value,
    };
  });

  function resetForm() {
    billingCompanyId.value = undefined;
    installationStatus.value = undefined;
    gatewayStatus.value = undefined;
    gatewayType.value = undefined;
    address.value = undefined;
    distanceFromCoords.value = undefined;
    utilityTypes.value = UtilityTypesOrderedList.map((ut) => ut.id);
    alertTypes.value = AlertTypeOptions.map((o) => o.value);
  }

  function initForm(f?: PropertyFilterForm) {
    billingCompanyId.value = f?.billingCompanyId;
    installationStatus.value = f?.installationStatus;
    gatewayStatus.value = f?.gatewayStatus;
    gatewayType.value = f?.gatewayType;
    address.value = f?.address;
    distanceFromCoords.value = f?.distanceFromCoords;
    utilityTypes.value = f?.utilityTypes || UtilityTypesOrderedList.map((ut) => ut.id);
    alertTypes.value = f?.alertTypes || AlertTypeOptions.map((o) => o.value);
  }

  watch(address, (val) => {
    if (val && !distanceFromCoords.value) {
      distanceFromCoords.value = DistanceOptions.value[1].value;
    }
    if (!val && distanceFromCoords.value) {
      distanceFromCoords.value = undefined;
    }
  });

  return {
    filterForm,
    hasFilterApplied,
    billingCompanyId,
    installationStatus,
    gatewayStatus,
    gatewayType,
    address,
    distanceFromCoords,
    utilityTypes,
    alertTypes,
    resetForm,
    initForm,
    DistanceOptions,
    distanceUom,
  };
}

export const usePropertyFilter = defineStore('property-filter', () => {
  const propertyService = new PropertyService();
  const {
    filterForm,
    hasFilterApplied,
    billingCompanyId,
    installationStatus,
    gatewayStatus,
    gatewayType,
    address,
    distanceFromCoords,
    utilityTypes,
    alertTypes,
    resetForm,
    initForm,
    DistanceOptions,
    distanceUom,
  } = usePropertyFilterForm();
  const loadingCounts = ref(false);

  const counts = computedAsync(() => getCounts(filterForm.value), { total: 0, filtered: 0 }, loadingCounts);

  function filterToQuery(f?: PropertyFilterForm): PropertyListCountQuery {
    const query: PropertyListCountQuery = {};
    if (!f) {
      return query;
    }
    if (f.billingCompanyId) {
      query.billingCompanyId = f.billingCompanyId;
    }
    if (f.installationStatus) {
      query.installationStatus = f.installationStatus;
    }
    if (f.address && f.distanceFromCoords) {
      query.withinRange = {
        coords: f.address.location.coordinates,
        maxDistance: f.distanceFromCoords,
        uom: distanceUom.value,
      };
    }
    if (f.gatewayStatus) {
      query.gatewayStatus = f.gatewayStatus;
    }
    if (f.gatewayType) {
      query.gatewayType = f.gatewayType;
    }
    // Only include filter for utility types if its something other than all of them
    if (f.utilityTypes && f.utilityTypes.length < UtilityTypesOrderedList.length) {
      query.utilityTypes = f.utilityTypes;
    }
    if (f.alertTypes && f.alertTypes.length < AlertTypeOptions.length) {
      query.alertTypes = f.alertTypes;
    }
    return query;
  }

  async function getCounts(f: PropertyFilterForm) {
    const total = await propertyService.countForUser({});
    const filtered = await propertyService.countForUser(filterToQuery(f));
    return { total, filtered };
  }

  return {
    filterForm,
    counts,
    resetForm,
    initForm,
    billingCompanyId,
    installationStatus,
    gatewayStatus,
    gatewayType,
    address,
    distanceFromCoords,
    utilityTypes,
    alertTypes,
    hasFilterApplied,
    filterToQuery,
    DistanceOptions,
    loadingCounts,
  };
});
